import DOMPurify from 'dompurify';
import sanitizeHtml, { defaultSanitizeConfig } from '~/utils/sanitize-html';
import React from 'react';
import Link from '~/components/common/link';
import { ProductRenewalType } from '~/store/types';
import { TextParagraphBold, UnorderedList } from '~/theme/elements';
import { useTranslation } from 'react-i18next';
import { useProfileTier } from '~/hooks/useProfileTier';
import { PROFILE_TIERS } from '~/constants/profileTier';

export interface Item {
    text: string;
    link?: string;
    url?: string;
    other?: string;
    openInNewWindow?: string;
    html?: boolean;
    condition?: ProductRenewalType[];
}
export interface ItemsListProps {
    haveHeader: boolean;
    heading: string;
    items: Item[];
    condition?: ProductRenewalType;
}

export default function CustomList({ haveHeader, heading, items, condition }: ItemsListProps) {
    const { t } = useTranslation('product-renewal');
    const { profileTier } = useProfileTier();

    const partition = 'verificationRequired';
    const specialItem = t(`${partition}.specialItem`);

    DOMPurify.addHook('afterSanitizeAttributes', async (node: Element) => {
        if ('target' in node) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener');
        }
    });

    return (
        <>
            {haveHeader && <TextParagraphBold>{heading}</TextParagraphBold>}
            <UnorderedList>
                {items.map((item, index) => {
                    if (!item.condition || (condition && item.condition.includes(condition))) {
                        if (!item.html) {
                            return (
                                <>
                                    {!(
                                        item.text === specialItem &&
                                        [PROFILE_TIERS.T1, PROFILE_TIERS.T2].includes(profileTier)
                                    ) && (
                                        <li
                                            id={`custom-list-item-${item.text}-${index}`}
                                            key={`custom-list-item-key-${index}`}
                                        >
                                            {item.text}{' '}
                                            {item.link && (
                                                <Link
                                                    openInNewWindow={
                                                        item.openInNewWindow === 'true'
                                                    }
                                                    href={item.url}
                                                >
                                                    {item.link}
                                                </Link>
                                            )}
                                            {item.other && <span>{item.other}</span>}
                                        </li>
                                    )}
                                </>
                            );
                        } else {
                            return (
                                <li
                                    id={`custom-list-item-${item.text}-${index}`}
                                    key={`custom-list-item-key-${index}`}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(item.text, defaultSanitizeConfig),
                                    }}
                                />
                            );
                        }
                    } else {
                        return null;
                    }
                })}
            </UnorderedList>
        </>
    );
}
